import { useState } from "react";
import { useAccess, useBillingState, useViewingAs } from "../bridges/frame";
import { useAccess as useNextAccess } from "../access/api";
import { useCurrentUser, useIsSuperAdmin } from "../bridges/session";
import { SWITCHBOARD } from "../routes/switchboard";
import type { Perm } from "../access/api";
import { buildAdminNav, buildMainNav, NavItem, showsTutorials } from "./nav";

const KEY = "cy_admin_menu";
/** Staff pages that exist only in the new layer: their menu links appear when their address is served by the new page (read per render: preview people get them early). */
const liveNow = () => new Set(SWITCHBOARD.filter((e) => e.mode === "next").map((e) => e.path));
const read = () => { try { return localStorage.getItem(KEY) === "1"; } catch { return false; } };

export const useNav = () => {
  const access = useAccess();
  const billing = useBillingState();
  const isSuperAdmin = useIsSuperAdmin();
  const viewing = useViewingAs();
  const next = useNextAccess();
  const user = useCurrentUser();
  // Once the access answer is here (and its last refresh did not fail) the menu follows permissions; before that, today's rule.
  const granted = next.data && !next.error ? next.data : null;
  const byPermission = granted
    ? { can: (p: Perm) => granted.permissions.includes(p), teamAllowed: granted.workspace.teamAllowed, reachAll: granted.reach.all, hasRoutes: granted.hasRoutes }
    : {};
  const ctx = { ...access, ...byPermission, isSuperAdmin: isSuperAdmin && !viewing.active, staffAccess: user?.staff_access ?? null, livePaths: liveNow(), locked: billing.locked, contactCount: billing.contactCount };
  const main = buildMainNav(ctx);
  const admin = buildAdminNav(ctx);
  const canSwitch = admin.length > 0;
  const [stored, setShow] = useState<boolean>(read);
  const setShowAdmin = (on: boolean) => { setShow(on); try { on ? localStorage.setItem(KEY, "1") : localStorage.removeItem(KEY); } catch { /* ignore */ } };
  const showAdmin = canSwitch && stored;
  const items: NavItem[] = showAdmin ? admin : main;
  const tutorials = showsTutorials(ctx);
  return { tutorials, main, admin, items, showAdmin, setShowAdmin, canSwitch };
};
